import React, { Component } from "react";
import {
  message,
  PageHeader,
  Spin,
  Row,
  Col,
  Select,
  Steps,
  Table,
  Tag,
  Tooltip,
  Checkbox,
  Progress,
  Result,
  Alert,
  Input,
  DatePicker,
} from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import { connect } from "react-redux";
import {
  ButtonItem,
  SelectItem,
  SelectInsumosItem,
  round2Decimals,
  round6Decimals,
} from "../../../../Components/Items";
import { extraerDatosDesdeArbol, textoTraslados, inferirPersonaPorRfc } from "../../ArchivosXml/xmlConceptosHelpers";
import ConceptosXmlPorXmlId from "../../ArchivosXml/ConceptosXmlPorXmlId";

const { Option } = Select;
const { Step } = Steps;
const { RangePicker } = DatePicker;

moment.locale("es");

const MONEDA_MXN_ID = "60c7e4396465ad440c41c15a";

// Copiado tal cual de AgregarFactura.js (FacturasSinOrdenDeCompra) para mantener
// exactamente el mismo cálculo de impuestos/retenciones por línea de insumo.
function calcularTotalesInsumo(insumoRow, proveedor) {
  const insumo = insumoRow.insumoCompleto;
  if (!insumo) return insumoRow;

  const precioUnitario = Number(insumoRow.precioUnitario || 0);
  const cantidad = insumoRow.cantidad;
  const descuento = insumoRow.descuento || 0;

  let totalImpuestosPrecioSinImpuestos = 0;
  for (const imp of insumo.impuestos) {
    let t = 0;
    if (imp.tasaCuota === 1) t = precioUnitario * (imp.monto / 100);
    else if (imp.tasaCuota === 2) t = (precioUnitario + totalImpuestosPrecioSinImpuestos) * (imp.monto / 100);
    else if (imp.tasaCuota === 3) t = imp.monto;
    else if (imp.tasaCuota === 4) t = cantidad ? (insumoRow.impuestos || 0) / cantidad : 0;
    totalImpuestosPrecioSinImpuestos += t;
  }
  const precioUnitarioConImpuestos = round2Decimals(precioUnitario + totalImpuestosPrecioSinImpuestos);

  if (!((precioUnitario || precioUnitario === 0) && cantidad)) {
    return { ...insumoRow, precioUnitarioConImpuestos };
  }

  const subtotalBruto = round6Decimals(cantidad * precioUnitario);
  const totalDescuento = round6Decimals(subtotalBruto * (descuento / 100));
  const totalEntrada = round6Decimals(subtotalBruto - totalDescuento);

  let totalImpuestos = 0;
  const arrayImpuestos = insumo.impuestos.map((imp) => {
    let t = 0;
    if (imp.tasaCuota === 1) t = round6Decimals(totalEntrada * (imp.monto / 100));
    else if (imp.tasaCuota === 2) t = round6Decimals((totalEntrada + totalImpuestos) * (imp.monto / 100));
    else if (imp.tasaCuota === 3) t = round6Decimals(cantidad * imp.monto);
    else if (imp.tasaCuota === 4) t = round6Decimals(insumoRow.impuestos || 0);
    totalImpuestos += t;
    return { descripcionFactura: imp.descripcionFactura, totalImpuestos: t };
  });

  const total = round2Decimals(totalEntrada + totalImpuestos);

  let totalRetenciones = 0;
  const arrayRetenciones = [];

  if (proveedor) {
    const procesarRetencion = (ret, condicion = true) => {
      if (!condicion) return;
      let t = 0;
      if (ret.tasaCuota === 1) t = round6Decimals(totalEntrada * (ret.monto / 100));
      else if (ret.tasaCuota === 2) t = round6Decimals((totalEntrada + totalRetenciones) * (ret.monto / 100));
      totalRetenciones += t;
      arrayRetenciones.push({ descripcionFactura: ret.descripcionFactura, totalRetenciones: t });
    };

    for (const ret of (proveedor.retenciones || [])) {
      procesarRetencion(ret);
    }
    for (const ret of (insumo.retenciones || [])) {
      const aplica =
        (ret.aplicaCompra === 1 && !proveedor.persona) ||
        (ret.aplicaCompra === 2 && proveedor.persona) ||
        ret.aplicaCompra === 3;
      procesarRetencion(ret, aplica);
    }
  }

  return {
    ...insumoRow,
    precioUnitarioConImpuestos,
    subtotal: totalEntrada,
    impuestos: totalImpuestos,
    arrayImpuestos,
    total,
    retenciones: totalRetenciones,
    arrayRetenciones,
  };
}

const EPSILON_TASA = 0.0005;

// Normaliza los traslados reales de un concepto del XML a una firma comparable.
// Un traslado "Tasa" con TasaOCuota=0 se trata igual que "Exento": algunos emisores
// declaran así el 0% aunque el catálogo SAT pida TipoFactor=Exento.
function normalizarFirmaConcepto(traslados) {
  return (traslados || [])
    .map((t) => {
      const exento = t.tipoFactor === "Exento" || (t.tipoFactor === "Tasa" && Number(t.tasaOCuota) === 0);
      return {
        codigo: t.impuestoCodigo,
        exento,
        esCuota: t.tipoFactor === "Cuota",
        valor: exento ? 0 : Number(t.tasaOCuota),
      };
    })
    .sort((a, b) => (a.codigo || "").localeCompare(b.codigo || ""));
}

// Normaliza los impuestos configurados en un insumo (ya poblados desde el backend
// vía impuestosRetencionesConsulta) a la misma forma. impuesto: 2=IVA ("002"), 3=IEPS ("003").
function normalizarFirmaInsumo(impuestosInsumo) {
  return (impuestosInsumo || [])
    .map((imp) => ({
      codigo: Number(imp.impuesto) === 2 ? "002" : Number(imp.impuesto) === 3 ? "003" : null,
      tasaCuota: imp.tasaCuota,
      valor: imp.tasaCuota === 3 ? Number(imp.monto) : Number(imp.monto) / 100,
    }))
    .sort((a, b) => (a.codigo || "").localeCompare(b.codigo || ""));
}

// Compara la firma fiscal real de un concepto del XML contra la firma configurada de
// un insumo candidato. El match debe ser exacto en ambas direcciones (mismo número de
// impuestos, cada uno con contraparte) para no calcular impuestos que no existen en la
// factura ni dejar de calcular alguno que sí trae.
function firmasCompatibles(firmaConcepto, firmaInsumo) {
  if (firmaInsumo.some((i) => i.codigo === null)) return false; // impuesto local (sin código SAT): nunca matchea un traslado del XML
  if (firmaConcepto.length !== firmaInsumo.length) return false;

  // tasaCuota=4 ("Elegible"/combustible) toma tal cual el importe combinado del concepto
  // (ver calcularTotalesInsumo). Si el concepto trae más de un impuesto, combinarlo con una
  // tasa explícita para otro código duplicaría el cálculo, así que el comodín sólo se acepta
  // cuando el concepto trae un único impuesto.
  const tieneComodin = firmaInsumo.some((i) => i.tasaCuota === 4);
  if (tieneComodin && firmaConcepto.length !== 1) return false;

  return firmaInsumo.every((impEntry) => {
    const match = firmaConcepto.find((c) => c.codigo === impEntry.codigo);
    if (!match) return false;
    if (impEntry.tasaCuota === 4) return true;
    if (impEntry.tasaCuota === 5) return match.exento;
    if (match.exento) return false;
    const esCuotaInsumo = impEntry.tasaCuota === 3;
    if (esCuotaInsumo !== match.esCuota) return false;
    return Math.abs(match.valor - impEntry.valor) < EPSILON_TASA;
  });
}

// Suma las líneas ya calculadas de una factura en los totales que espera Factura.js,
// replicando la agregación que hoy hace AgregarFactura.onFinish (sin ajustes manuales,
// ya que en el importador masivo no hay edición línea por línea).
function agregarTotalesFactura(insumosCalculados) {
  let total = 0;
  let subtotal = 0;
  let cuentasProveedor = 0;
  let arrayImpuestos = [];
  let arrayRetenciones = [];

  insumosCalculados.forEach((value) => {
    total += value.subtotal;
    subtotal += value.subtotal;

    (value.arrayImpuestos || []).forEach((valueImpuestos) => {
      const index = arrayImpuestos.findIndex((v) => v.descripcionFactura === valueImpuestos.descripcionFactura);
      if (index !== -1) {
        arrayImpuestos[index] = {
          descripcionFactura: valueImpuestos.descripcionFactura,
          totalImpuestos: arrayImpuestos[index].totalImpuestos + valueImpuestos.totalImpuestos,
        };
      } else {
        arrayImpuestos.push({ descripcionFactura: valueImpuestos.descripcionFactura, totalImpuestos: valueImpuestos.totalImpuestos });
      }
    });

    (value.arrayRetenciones || []).forEach((valueRetenciones) => {
      cuentasProveedor += round2Decimals(valueRetenciones.totalRetenciones);
      const index = arrayRetenciones.findIndex((v) => v.descripcionFactura === valueRetenciones.descripcionFactura);
      if (index !== -1) {
        arrayRetenciones[index] = {
          descripcionFactura: valueRetenciones.descripcionFactura,
          totalRetenciones: arrayRetenciones[index].totalRetenciones + valueRetenciones.totalRetenciones,
        };
      } else {
        arrayRetenciones.push({ descripcionFactura: valueRetenciones.descripcionFactura, totalRetenciones: valueRetenciones.totalRetenciones });
      }
    });
  });

  arrayImpuestos = arrayImpuestos.map((valueImpuesto) => ({
    ...valueImpuesto,
    ajusteImpuesto: 0,
    totalImpuestos: round2Decimals(valueImpuesto.totalImpuestos),
  }));
  arrayRetenciones = arrayRetenciones.map((valueRetencion) => ({ ...valueRetencion, ajusteRetencion: 0 }));

  arrayImpuestos.forEach((valueImpuesto) => {
    total += valueImpuesto.totalImpuestos;
  });

  total = round2Decimals(total);
  subtotal = round2Decimals(subtotal);
  cuentasProveedor = round2Decimals(cuentasProveedor);

  return {
    total,
    subtotal,
    cuentasProveedor,
    totalPagar: round2Decimals(total - cuentasProveedor),
    arrayImpuestos,
    arrayRetenciones,
  };
}


class ImportarFacturasXmlMasivo extends Component {
  state = {
    loading: false,
    paso: 0,

    empresaRfc: null,
    todasProveedores: [],
    todasProyectos: [],
    objetoProyectos: null,
    todasMonedas: [],
    todasUsosDeCFDI: [],
    todasFormasDePago: [],
    todasTiposDeProveedor: [],

    cargandoLista: false,
    xmlsDisponibles: [],
    seleccionadosKeys: [],
    filtroUsoCfdi: [],
    filtroTextoXml: "",
    filtroRangoFechasXml: null,
    archivos: [],

    tipoProveedorId: undefined,

    proyectoId: undefined,
    subProyectoId: undefined,
    subProyectosProyecto: [],

    filas: [],
    guardando: false,
    terminado: false,
    progreso: { actual: 0, total: 0 },
    resultados: [],

    seleccionGruposKeys: [],
    insumoRelacionarTemp: null,
    relacionandoLote: false,
    omitidasPorImpuestos: [],
  };

  componentDidMount() {
    this.setState({ loading: true });
    Promise.all([this.consultarDatos(), this.consultarXmlsDisponibles()]).finally(() => this.setState({ loading: false }));
  }

  async consultarDatos() {
    const [resFactura, resTipos] = await Promise.all([
      axios.post(
        "facturasSinOrdenDeCompra/selectDatosParaFacturaSinOrdenDeCompra",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      ),
      axios.post(
        "tiposdeproveedores/selectTiposDeProveedores",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      ),
    ]);

    const newState = {};

    if (resFactura.data.configuracion) {
      newState.empresaRfc = resFactura.data.configuracion.rfc;
    } else {
      message.error("No se encontró la configuración de esta empresa");
    }

    newState.todasProveedores = resFactura.data.proveedores || [];
    newState.todasProyectos = resFactura.data.proyectos || [];
    newState.objetoProyectos = (resFactura.data.proyectos || []).map((v) => (
      <Option value={v._id} key={v._id}>{v.nombre}</Option>
    ));
    newState.todasMonedas = resFactura.data.monedas || [];
    newState.todasUsosDeCFDI = resFactura.data.usosDeCFDI || [];
    newState.todasFormasDePago = resFactura.data.formasDePago || [];

    if (Array.isArray(resTipos.data)) {
      newState.todasTiposDeProveedor = resTipos.data;
    }

    this.setState(newState);
  }

  // Alta de un proveedor hecha desde la vista de conceptos del Paso 1 (antes de llegar
  // al paso "Proveedor"), para que avanzarAPaso3 no intente darlo de alta de nuevo.
  onProveedorAlta(proveedorNuevo) {
    this.setState((prev) => ({ todasProveedores: [...prev.todasProveedores, proveedorNuevo] }));
  }

  // ---------- Paso 1: elegir XMLs ya descargados en el sistema ----------

  async consultarXmlsDisponibles() {
    this.setState({ cargandoLista: true });
    try {
      const res = await axios.post(
        "descargaMasiva/selectTodosXml",
        { empresaId: this.props.empresaId, valor: 1, filtroTipo: "I", page: 1, pageSize: 500 },
        { headers: this.props.headersToken }
      );
      if (res.data.success === 2) {
        // Mismo criterio que habilita el botón "Agregar factura sin orden de compra"
        // en ArchivosXml.js: contabilizada 1 (ya filtrado por valor:1), vigente y no en cancelación.
        const disponibles = (res.data.data || []).filter(
          (r) => (r.estado === 1 || r.estado === 6) && r.estadoCancelacion !== 1
        );
        this.setState({ xmlsDisponibles: disponibles });
      } else {
        this.setState({ xmlsDisponibles: [] });
      }
    } finally {
      this.setState({ cargandoLista: false });
    }
  }

  async obtenerArchivoDesdeXmlDescargado(row) {
    const nombre = `${row.serie || ""} ${row.folio || ""}`.trim() || row.emisorNombre || row.uuid;
    try {
      const [resDatos, resXml] = await Promise.all([
        axios.post("descargaMasiva/datosXml", { xmlId: row._id, empresaId: this.props.empresaId }, { headers: this.props.headersToken }),
        axios.post("descargaMasiva/descargarXml", { xmlId: row._id, empresaId: this.props.empresaId }, { headers: this.props.headersToken }),
      ]);

      if (resDatos.data.success !== 2) {
        return { uid: row._id, nombre, estado: "error", motivo: "No se pudo leer el XML" };
      }

      const datos = extraerDatosDesdeArbol(resDatos.data.dataXml);
      if (!datos.uuid) datos.uuid = row.uuid;

      if (datos.tipoDeComprobante !== "I") {
        return { uid: row._id, nombre, estado: "error", motivo: "El XML no es una factura de ingreso" };
      }
      if ((datos.rfcReceptor || "").toUpperCase() !== (this.state.empresaRfc || "").toUpperCase()) {
        return { uid: row._id, nombre, estado: "error", motivo: "El RFC del receptor no coincide con el de la empresa" };
      }
      if (!datos.conceptos.length) {
        return { uid: row._id, nombre, estado: "error", motivo: "El XML no tiene conceptos" };
      }

      let estado = "valido";
      try {
        const resUuid = await axios.post(
          "facturas/checarUUID",
          { empresaId: this.props.empresaId, uuid: datos.uuid },
          { headers: this.props.headersToken }
        );
        if (resUuid.data.existeUUID) estado = "duplicado";
      } catch (e) {
        // si falla la validación de duplicado se deja pasar; se revisa igual en la tabla de revisión
      }

      const xmlTexto = resXml.data.success === 2 ? resXml.data.xml : undefined;

      return { uid: row._id, nombre, estado, xmlTexto, datos };
    } catch (e) {
      return { uid: row._id, nombre, estado: "error", motivo: "Error al leer el XML" };
    }
  }

  async avanzarAPaso2() {
    const { xmlsDisponibles, seleccionadosKeys } = this.state;
    const filas = xmlsDisponibles.filter((r) => seleccionadosKeys.includes(r._id));

    this.setState({ loading: true });
    const archivos = await Promise.all(filas.map((r) => this.obtenerArchivoDesdeXmlDescargado(r)));
    this.setState({ archivos, loading: false, paso: 1 });
  }

  textoUsoCfdi(codigo) {
    if (!codigo) return "-";
    const uso = this.state.todasUsosDeCFDI.find((u) => u.c_UsoCFDI === codigo);
    return uso ? `${uso.c_UsoCFDI} - ${uso.descripcion}` : codigo;
  }

  // Filtra la lista de XMLs disponibles por Uso de CFDI, texto libre (serie, folio,
  // emisor, RFC o UUID) y rango de fechas, todo combinable entre sí.
  xmlsDisponiblesFiltrados() {
    const { xmlsDisponibles, filtroUsoCfdi, filtroTextoXml, filtroRangoFechasXml } = this.state;

    let resultado = xmlsDisponibles;

    if (filtroUsoCfdi.length > 0) {
      resultado = resultado.filter((r) => filtroUsoCfdi.includes(r.usoDeCfdi));
    }

    const texto = (filtroTextoXml || "").trim().toLowerCase();
    if (texto) {
      resultado = resultado.filter((r) => {
        const rfc = r.emisor?._attributes?.Rfc || "";
        const campos = [r.serie, r.folio, r.emisorNombre, rfc, r.uuid];
        return campos.some((c) => (c || "").toLowerCase().includes(texto));
      });
    }

    const [fechaDesde, fechaHasta] = filtroRangoFechasXml || [];
    if (fechaDesde) resultado = resultado.filter((r) => r.fecha && moment(r.fecha).isSameOrAfter(fechaDesde, "day"));
    if (fechaHasta) resultado = resultado.filter((r) => r.fecha && moment(r.fecha).isSameOrBefore(fechaHasta, "day"));

    return resultado;
  }

  renderPaso1() {
    const { xmlsDisponibles, seleccionadosKeys, cargandoLista, filtroUsoCfdi, filtroTextoXml, filtroRangoFechasXml } = this.state;

    const codigosUsoCfdiDisponibles = Array.from(
      new Set(xmlsDisponibles.map((r) => r.usoDeCfdi).filter(Boolean))
    );
    const xmlsFiltrados = this.xmlsDisponiblesFiltrados();
    const hayFiltrosActivos = filtroUsoCfdi.length > 0 || !!filtroTextoXml || !!filtroRangoFechasXml;

    return (
      <>
        <Row justify="space-between" style={{ marginBottom: 8 }}>
          <Col><p>Elige los XML ya descargados en el sistema que quieres contabilizar:</p></Col>
          <Col>
            <ButtonItem onClick={() => this.consultarXmlsDisponibles()} loading={cargandoLista}>
              Actualizar lista
            </ButtonItem>
          </Col>
        </Row>

        <Row gutter={8} style={{ marginBottom: 8 }} align="middle">
          <Col xs={24} sm={8}>
            <Input.Search
              allowClear
              placeholder="Buscar por serie, folio, emisor, RFC o UUID"
              value={filtroTextoXml}
              onChange={(e) => this.setState({ filtroTextoXml: e.target.value })}
            />
          </Col>
          <Col xs={24} sm={8}>
            <RangePicker
              allowClear
              style={{ width: "100%" }}
              placeholder={["Fecha desde", "Fecha hasta"]}
              value={filtroRangoFechasXml}
              onChange={(v) => this.setState({ filtroRangoFechasXml: v })}
            />
          </Col>
          {codigosUsoCfdiDisponibles.length > 0 && (
            <Col xs={24} sm={8}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder="Filtrar por Uso de CFDI"
                value={filtroUsoCfdi}
                onChange={(v) => this.setState({ filtroUsoCfdi: v })}
              >
                {codigosUsoCfdiDisponibles.map((codigo) => (
                  <Option value={codigo} key={codigo}>{this.textoUsoCfdi(codigo)}</Option>
                ))}
              </Select>
            </Col>
          )}
        </Row>

        {hayFiltrosActivos && (
          <Row justify="space-between" style={{ marginBottom: 8 }}>
            <Col><Tag color="blue">{xmlsFiltrados.length} de {xmlsDisponibles.length} XML(s) con los filtros aplicados</Tag></Col>
            <Col>
              <ButtonItem
                size="small"
                onClick={() => this.setState({ filtroUsoCfdi: [], filtroTextoXml: "", filtroRangoFechasXml: null })}
              >
                Limpiar filtros
              </ButtonItem>
            </Col>
          </Row>
        )}

        <Table
          size="small"
          rowKey="_id"
          loading={cargandoLista}
          dataSource={xmlsFiltrados}
          pagination={{ defaultPageSize: 10 }}
          rowSelection={{
            selectedRowKeys: seleccionadosKeys,
            onChange: (keys) => this.setState({ seleccionadosKeys: keys }),
          }}
          expandable={{
            rowExpandable: () => true,
            expandedRowRender: (record) => (
              <ConceptosXmlPorXmlId
                empresaId={this.props.empresaId}
                headersToken={this.props.headersToken}
                xmlId={record._id}
                todasProveedores={this.state.todasProveedores}
                todasTiposDeProveedor={this.state.todasTiposDeProveedor}
                todasMonedas={this.state.todasMonedas}
                onProveedorAlta={this.onProveedorAlta.bind(this)}
              />
            ),
          }}
          columns={[
            {
              title: "Serie/Folio",
              render: (_, r) => `${r.serie || ""} ${r.folio || ""}`.trim() || "-",
              sorter: (a, b) => `${a.serie || ""}${a.folio || ""}`.localeCompare(`${b.serie || ""}${b.folio || ""}`),
            },
            {
              title: "Emisor",
              dataIndex: "emisorNombre",
              sorter: (a, b) => (a.emisorNombre || "").localeCompare(b.emisorNombre || ""),
            },
            { title: "RFC", render: (_, r) => r.emisor?._attributes?.Rfc || "-" },
            { title: "UUID", dataIndex: "uuid" },
            { title: "Uso CFDI", render: (_, r) => this.textoUsoCfdi(r.usoDeCfdi) },
            {
              title: "Fecha",
              render: (_, r) => (r.fecha ? moment(r.fecha).format("L") : "-"),
              sorter: (a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0),
              defaultSortOrder: "descend",
            },
            {
              title: "Total",
              render: (_, r) => (typeof r.total === "number" ? r.total.toFixed(2) : "-"),
              align: "right",
              sorter: (a, b) => (a.total || 0) - (b.total || 0),
            },
          ]}
        />

        <Row justify="end" style={{ marginTop: 20 }}>
          <ButtonItem type="primary" loading={this.state.loading} disabled={seleccionadosKeys.length === 0} onClick={() => this.avanzarAPaso2()}>
            Siguiente ({seleccionadosKeys.length})
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 2: tipo de proveedor ----------

  calcularRfcsNuevos() {
    const { archivos, todasProveedores } = this.state;
    const vistos = new Set();
    const resultado = [];
    archivos
      .filter((a) => a.estado === "valido" || a.estado === "duplicado")
      .forEach((a) => {
        const rfc = a.datos.rfcEmisor;
        if (!rfc || vistos.has(rfc.toUpperCase())) return;
        vistos.add(rfc.toUpperCase());
        const existe = todasProveedores.find((p) => (p.rfc || "").toUpperCase() === rfc.toUpperCase());
        if (!existe) resultado.push({ rfc, nombre: a.datos.nombreEmisor });
      });
    return resultado;
  }

  async avanzarAPaso3() {
    if (!this.state.tipoProveedorId) {
      message.error("Selecciona un tipo de proveedor");
      return;
    }

    const rfcsNuevos = this.calcularRfcsNuevos();
    if (rfcsNuevos.length === 0) {
      this.setState({ paso: 2 });
      return;
    }

    this.setState({ loading: true });
    const proveedoresActualizados = [...this.state.todasProveedores];

    for (const { rfc, nombre } of rfcsNuevos) {
      const persona = inferirPersonaPorRfc(rfc);
      const body = {
        empresaId: this.props.empresaId,
        tipoProveedorId: this.state.tipoProveedorId,
        rfc,
        persona,
        nombre: !persona ? nombre : "",
        razonSocial: persona ? nombre : "",
        monedaId: MONEDA_MXN_ID,
        tipoOperacion: 3,
        tipoTercero: 1,
        pagoElectronico: false,
        estado: 1,
      };

      try {
        const res = await axios.post("proveedores/agregarProveedor", body, { headers: this.props.headersToken });
        if (res.data.success === 2) {
          proveedoresActualizados.push({ ...body, _id: res.data.codigo, diasCredito: 0, retenciones: [] });
        } else {
          message.warning(`No se pudo dar de alta el proveedor con RFC ${rfc}: ${res.data.message || "error desconocido"}`);
        }
      } catch (e) {
        message.warning(`Error al dar de alta el proveedor con RFC ${rfc}`);
      }
    }

    this.setState({ todasProveedores: proveedoresActualizados, loading: false, paso: 2 });
  }

  renderPaso2() {
    const rfcsNuevos = this.calcularRfcsNuevos();
    const errores = this.state.archivos.filter((a) => a.estado === "error");
    const duplicados = this.state.archivos.filter((a) => a.estado === "duplicado");

    return (
      <>
        {errores.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            message={`${errores.length} XML no se pudieron incluir`}
            description={errores.map((e) => `${e.nombre}: ${e.motivo}`).join(" · ")}
          />
        )}
        {duplicados.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message={`${duplicados.length} XML ya tienen una factura con ese UUID`}
            description="Quedarán marcados como duplicados en la revisión final y no se pueden guardar."
          />
        )}

        <Row>
          <Col xs={24} sm={12}>
            <p>Tipo de proveedor con el que se darán de alta los proveedores nuevos:</p>
            <Select
              style={{ width: "100%" }}
              placeholder="Tipo de proveedor"
              value={this.state.tipoProveedorId}
              onChange={(v) => this.setState({ tipoProveedorId: v })}
            >
              {this.state.todasTiposDeProveedor.map((t) => (
                <Option value={t._id} key={t._id}>{t.nombre}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        {rfcsNuevos.length > 0 && (
          <Alert
            style={{ marginTop: 16 }}
            type="info"
            showIcon
            message={`Se darán de alta ${rfcsNuevos.length} proveedor(es) nuevo(s)`}
            description={rfcsNuevos.map((r) => `${r.nombre} (${r.rfc})`).join(", ")}
          />
        )}

        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 0 })}>Atrás</ButtonItem>
          <ButtonItem type="primary" onClick={() => this.avanzarAPaso3()}>Siguiente</ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 3: proyecto ----------

  onChangeProyectoWizard = (value) => {
    const proyecto = this.state.todasProyectos.find((p) => p._id === value);
    this.setState({ proyectoId: value, subProyectoId: undefined, subProyectosProyecto: proyecto?.subProyectos || [] });
  };

  async avanzarAPasoConceptos() {
    this.setState({ loading: true });
    await this.construirFilasYResolverInsumos();
    this.setState({ loading: false, paso: 3 });
  }

  renderPasoProyecto() {
    return (
      <>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <p>Proyecto en el que se registrarán todas las facturas de este lote:</p>
            <SelectItem placeholder="Proyecto" style={{ width: "100%" }} value={this.state.proyectoId} onChange={this.onChangeProyectoWizard}>
              {this.state.objetoProyectos}
            </SelectItem>
          </Col>
          {this.state.subProyectosProyecto.length > 0 && (
            <Col xs={24} sm={12}>
              <p>Subproyecto:</p>
              <SelectItem
                placeholder="Subproyecto"
                style={{ width: "100%" }}
                allowClear
                value={this.state.subProyectoId}
                onChange={(v) => this.setState({ subProyectoId: v })}
              >
                {this.state.subProyectosProyecto.map((sp) => <Option value={sp._id} key={sp._id}>{sp.nombre}</Option>)}
              </SelectItem>
            </Col>
          )}
        </Row>
        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 1 })}>Atrás</ButtonItem>
          <ButtonItem
            type="primary"
            loading={this.state.loading}
            disabled={!this.state.proyectoId}
            onClick={() => this.avanzarAPasoConceptos()}
          >
            Siguiente
          </ButtonItem>
        </Row>
      </>
    );
  }

  // ---------- Paso 5: revisión y guardado ----------

  // Recalcula el estado/totales/payload de una factura a partir de los conceptos con su
  // insumo ya resuelto (automático o asignado a mano). Se usa tanto al construir las filas
  // por primera vez como al corregir manualmente el insumo de una línea en el paso de revisión.
  recalcularFila(archivo, conceptosConMatch, contexto) {
    const { proveedor, proyectoId, subProyectoId, todasMonedas, todasUsosDeCFDI, todasFormasDePago, empresaId } = contexto;
    const d = archivo.datos;
    const proveedorNombre = proveedor.stringBuscar || proveedor.razonSocial || proveedor.nombre;

    const faltantes = conceptosConMatch.filter((c) => !c.insumoMatch);
    if (faltantes.length > 0) {
      return {
        ...archivo,
        proveedorNombre,
        conceptosConMatch,
        filaEstado: "faltaAsignar",
        motivoFila: `${faltantes.length} línea(s) sin insumo asignado, revísalas abajo`,
        incluir: false,
        payload: undefined,
      };
    }

    const insumosCalculados = conceptosConMatch.map(({ concepto, insumoMatch }) =>
      calcularTotalesInsumo(
        {
          insumoId: insumoMatch._id,
          insumoCompleto: insumoMatch,
          precioUnitario: concepto.precioUnitario,
          cantidad: concepto.cantidad,
          descuento: concepto.descuento,
          impuestos: concepto.impuestos,
          proyectoId,
          subProyectoId,
        },
        proveedor
      )
    );

    const totales = agregarTotalesFactura(insumosCalculados);

    const moneda = todasMonedas.find((m) => (m.abreviacion || "").toLowerCase() === (d.monedaAbrev || "").toLowerCase());
    const monedaId = moneda ? moneda._id : MONEDA_MXN_ID;
    const usoDeCfdiId = todasUsosDeCFDI.find((u) => u.c_UsoCFDI === d.usoCfdiCodigo)?._id;
    const formaDePagoId = d.formaPagoCodigo
      ? todasFormasDePago.find((f) => {
          const n = Number(d.formaPagoCodigo);
          return f.c_FormaPago === (isNaN(n) ? d.formaPagoCodigo : n);
        })?._id
      : undefined;
    const metodoPago = d.metodoPagoStr === "PUE" ? 1 : d.metodoPagoStr === "PPD" ? 2 : undefined;

    const fechaFactura = moment(d.fechaFactura);
    const diasCredito = proveedor.diasCredito || 0;
    // Con el complemento vencimientoDesdeRecepcionFactura la recepcion es el dia de la
    // importacion (el backend la guarda asi) y de ahi corren los dias de credito.
    const fechaBaseVencimiento = this.props.vencimientoDesdeRecepcionFactura ? moment() : fechaFactura;
    const fechaPago = moment(fechaBaseVencimiento).add(diasCredito, "days");

    let filaEstado = archivo.estado === "duplicado" ? "duplicado" : "nuevo";
    let motivoFila = archivo.estado === "duplicado" ? "El UUID ya existe en otra factura" : undefined;

    const diferenciaTotales = round2Decimals(d.totalXml - totales.total);
    if (filaEstado === "nuevo" && Math.abs(diferenciaTotales) > 1) {
      filaEstado = "error";
      motivoFila = `El total calculado (${totales.total}) no coincide con el del XML (${d.totalXml})`;
    }

    const payload = {
      empresaId,
      proveedorId: proveedor._id,
      proyectoId,
      subProyectoId,
      insumos: insumosCalculados,
      arrayImpuestos: totales.arrayImpuestos,
      arrayRetenciones: totales.arrayRetenciones,
      total: totales.total,
      subtotal: totales.subtotal,
      cuentasProveedor: totales.cuentasProveedor,
      totalPagar: totales.totalPagar,
      ajusteSubtotal: 0,
      fechaFactura,
      fechaPago,
      fechaVencimiento: fechaPago,
      diasCredito,
      monedaId,
      tipoDeCambio: d.tipoDeCambio,
      usoDeCfdiId,
      formaDePagoId,
      metodoPago,
      uuid: d.uuid,
      codigoFactura: d.codigoFactura,
      xml: archivo.xmlTexto,
      recibirInsumos: true,
      estadoCC: false,
    };

    return {
      ...archivo,
      proveedorNombre,
      conceptosConMatch,
      filaEstado,
      motivoFila,
      incluir: filaEstado === "nuevo",
      payload,
    };
  }

  // Agrupa los conceptos de todas las facturas por proveedor y consulta insumos/procesarXml
  // una sola vez por proveedor (no por factura), para resolver qué insumo tiene ya relacionado
  // cada concepto. Cada concepto viaja con un _uidLinea propio porque procesarXml no garantiza
  // devolver los resultados en el mismo orden en que se envían (resuelve cada uno dentro de un
  // Promise.all interno), así que el mapeo de vuelta a cada línea se hace por ese id, no por índice.
  async construirFilasYResolverInsumos() {
    const { archivos, todasProveedores, proyectoId, subProyectoId, todasMonedas, todasUsosDeCFDI, todasFormasDePago } = this.state;
    const empresaId = this.props.empresaId;

    const archivosConProveedor = archivos
      .filter((a) => a.estado === "valido" || a.estado === "duplicado")
      .map((archivo) => ({
        archivo,
        proveedor: todasProveedores.find((p) => (p.rfc || "").toUpperCase() === (archivo.datos.rfcEmisor || "").toUpperCase()),
      }));

    const conceptosPorProveedor = new Map();
    archivosConProveedor.forEach(({ archivo, proveedor }) => {
      if (!proveedor) return;
      if (!conceptosPorProveedor.has(proveedor._id)) conceptosPorProveedor.set(proveedor._id, []);
      archivo.datos.conceptos.forEach((concepto, indice) => {
        conceptosPorProveedor.get(proveedor._id).push({ ...concepto, _uidLinea: `${archivo.uid}#${indice}` });
      });
    });

    const matchPorUid = new Map();
    const proveedoresConError = new Set();

    await Promise.all(
      Array.from(conceptosPorProveedor.entries()).map(async ([proveedorId, conceptos]) => {
        try {
          const res = await axios.post(
            "insumos/procesarXml",
            { empresaId, proveedorId, conceptos },
            { headers: this.props.headersToken }
          );
          if (res.data.success === 2) {
            (res.data.insumos || []).forEach((item) => {
              // insumoCompleto._id que devuelve procesarXml es el id del subdocumento
              // Insumo.relaciones, no el id real del Insumo (viene en insumoPrincipalId).
              // Se normaliza aquí para que SelectInsumosItem pueda precargarlo correctamente
              // y para saber, al relacionar, si hay que sobreescribir esa relación existente.
              const insumoMatch =
                item.insumoId && item.insumoCompleto
                  ? { ...item.insumoCompleto, _id: item.insumoCompleto.insumoPrincipalId, relacionId: item.insumoCompleto._id }
                  : null;
              matchPorUid.set(item._uidLinea, insumoMatch);
            });
          } else {
            proveedoresConError.add(proveedorId);
          }
        } catch (e) {
          proveedoresConError.add(proveedorId);
        }
      })
    );

    const contexto = { proyectoId, subProyectoId, todasMonedas, todasUsosDeCFDI, todasFormasDePago, empresaId };

    const filas = archivosConProveedor.map(({ archivo, proveedor }) => {
      if (!proveedor) {
        return { ...archivo, proveedorNombre: "-", filaEstado: "error", motivoFila: "No se pudo resolver el proveedor", incluir: false };
      }
      if (proveedoresConError.has(proveedor._id)) {
        return { ...archivo, proveedorNombre: proveedor.stringBuscar, filaEstado: "error", motivoFila: "No se pudieron resolver los insumos de este proveedor", incluir: false };
      }

      const conceptosConMatch = archivo.datos.conceptos.map((concepto, indice) => ({
        concepto,
        insumoMatch: matchPorUid.get(`${archivo.uid}#${indice}`) || null,
      }));

      return this.recalcularFila(archivo, conceptosConMatch, { ...contexto, proveedor });
    });

    this.setState({ filas });
  }

  marcarFila(uid, incluir) {
    this.setState((prev) => ({
      filas: prev.filas.map((f) => (f.uid === uid ? { ...f, incluir } : f)),
    }));
  }

  async guardarTodas() {
    const filasAGuardar = this.state.filas.filter((f) => f.incluir);
    this.setState({ guardando: true, progreso: { actual: 0, total: filasAGuardar.length } });

    const resultados = [];
    for (let i = 0; i < filasAGuardar.length; i++) {
      const fila = filasAGuardar[i];
      try {
        const res = await axios.post("facturassinordendecompra/agregarFactura", fila.payload, { headers: this.props.headersToken });
        if (res.data.success === 2) {
          resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: true });
        } else {
          resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: false, motivo: res.data.message || "Error al guardar" });
        }
      } catch (e) {
        resultados.push({ uid: fila.uid, nombre: fila.nombre, ok: false, motivo: "Error de comunicación con el servidor" });
      }
      this.setState({ progreso: { actual: i + 1, total: filasAGuardar.length } });
    }

    this.setState({ guardando: false, terminado: true, resultados });
  }

  // Agrupa TODAS las líneas de todas las facturas no-error (tengan o no ya un insumo
  // relacionado) por firma de concepto (proveedor + código + descripción + clave SAT), para
  // poder relacionarlas/corregirlas todas de una vez a un mismo insumo en vez de una por una.
  agruparTodasLasLineas() {
    const { filas, todasProveedores } = this.state;
    const grupos = new Map();

    filas.forEach((fila) => {
      if (fila.filaEstado === "error") return;
      const proveedor = todasProveedores.find(
        (p) => (p.rfc || "").toUpperCase() === (fila.datos.rfcEmisor || "").toUpperCase()
      );

      fila.conceptosConMatch.forEach((c, indice) => {
        const concepto = c.concepto;
        const key = [proveedor?._id, concepto.codigo, concepto.descripcion, concepto.claveProdServ, concepto.claveUnidad].join("|");

        if (!grupos.has(key)) {
          grupos.set(key, {
            key,
            proveedorId: proveedor?._id,
            proveedorNombre: fila.proveedorNombre,
            codigo: concepto.codigo,
            descripcion: concepto.descripcion,
            claveProdServ: concepto.claveProdServ,
            claveUnidad: concepto.claveUnidad,
            precioUnitario: concepto.precioUnitario,
            lineas: [],
          });
        }
        grupos.get(key).lineas.push({ uid: fila.uid, indice, concepto, facturaNombre: fila.nombre, insumoMatch: c.insumoMatch });
      });
    });

    // estadoInsumo "mixto" es defensivo: no debería ocurrir en condiciones normales, ya que la
    // clave de agrupación es la misma que usa procesarXml para resolver una única relación, pero
    // puede pasar si hay relaciones duplicadas/sucias en la base apuntando a insumos distintos.
    return Array.from(grupos.values()).map((grupo) => {
      const matches = grupo.lineas.map((l) => l.insumoMatch);
      const primero = matches[0];
      const primeroId = primero ? primero._id : null;
      const consistente = matches.every((m) => (m ? m._id : null) === primeroId);
      return {
        ...grupo,
        insumoActual: consistente ? primero : null,
        estadoInsumo: !consistente ? "mixto" : primero ? "relacionado" : "sinRelacionar",
        relacionIdActual: consistente && primero ? primero.relacionId : null,
      };
    });
  }

  // Relaciona los grupos marcados al insumo elegido. Por cada línea revisa si sus
  // impuestos reales coinciden con los configurados en el insumo (misma comparación que
  // el matching automático); las que no coinciden se dejan pendientes y se avisan, en vez
  // de relacionarse con una configuración fiscal que no les corresponde. Por cada grupo con
  // al menos una línea relacionada se guarda una relación nueva en el insumo (vía
  // insumos/relacionarInsumo) para que la próxima factura del mismo proveedor con ese mismo
  // concepto matchee sola.
  async relacionarGruposSeleccionados() {
    const { insumoRelacionarTemp, seleccionGruposKeys } = this.state;
    if (!insumoRelacionarTemp || seleccionGruposKeys.length === 0) return;

    // Se excluyen los grupos ya relacionados exactamente al insumo elegido: no hay nada
    // que hacer y evita llamadas innecesarias a insumos/relacionarInsumo.
    const grupos = this.agruparTodasLasLineas().filter(
      (g) => seleccionGruposKeys.includes(g.key) && !(g.insumoActual && g.insumoActual._id === insumoRelacionarTemp._id)
    );
    const firmaInsumo = normalizarFirmaInsumo(insumoRelacionarTemp.impuestos);

    this.setState({ relacionandoLote: true });

    const actualizaciones = [];
    const omitidas = [];

    for (const grupo of grupos) {
      let algunaRelacionada = false;
      for (const linea of grupo.lineas) {
        const firmaConcepto = normalizarFirmaConcepto(linea.concepto.traslados);
        if (firmasCompatibles(firmaConcepto, firmaInsumo)) {
          actualizaciones.push(linea);
          algunaRelacionada = true;
        } else {
          omitidas.push({ ...linea, grupo });
        }
      }

      if (algunaRelacionada) {
        try {
          await axios.post(
            "insumos/relacionarInsumo",
            {
              empresaId: this.props.empresaId,
              insumoId: insumoRelacionarTemp._id,
              proveedorId: grupo.proveedorId,
              codigoProveedorRelacion: grupo.codigo,
              descripcionProveedor: grupo.descripcion,
              claveProdServ: grupo.claveProdServ,
              claveUnidad: grupo.claveUnidad,
              precioUnitario: grupo.precioUnitario,
              conversion: 1,
              // Si el grupo ya tenía una relación previa (a otro insumo), hay que
              // sobreescribirla en vez de agregar una relación duplicada: sin esto,
              // procesarXml podría seguir matcheando la relación vieja más adelante.
              ...(grupo.relacionIdActual ? { sobreescribir: true, relacionId: grupo.relacionIdActual } : {}),
            },
            { headers: this.props.headersToken }
          );
        } catch (e) {
          message.warning(`No se pudo guardar la relación para "${grupo.descripcion}", pero la(s) factura(s) se actualizaron de todas formas`);
        }
      }
    }

    this.setState((prev) => {
      const seUsa = (uid, indice) => actualizaciones.some((a) => a.uid === uid && a.indice === indice);
      const filas = prev.filas.map((fila) => {
        if (!fila.conceptosConMatch.some((c, i) => seUsa(fila.uid, i))) return fila;
        const proveedor = prev.todasProveedores.find(
          (p) => (p.rfc || "").toUpperCase() === (fila.datos.rfcEmisor || "").toUpperCase()
        );
        const conceptosConMatch = fila.conceptosConMatch.map((c, i) =>
          seUsa(fila.uid, i) ? { ...c, insumoMatch: insumoRelacionarTemp } : c
        );
        return this.recalcularFila(fila, conceptosConMatch, {
          proveedor,
          proyectoId: prev.proyectoId,
          subProyectoId: prev.subProyectoId,
          todasMonedas: prev.todasMonedas,
          todasUsosDeCFDI: prev.todasUsosDeCFDI,
          todasFormasDePago: prev.todasFormasDePago,
          empresaId: this.props.empresaId,
        });
      });
      return { filas };
    });

    if (actualizaciones.length > 0) {
      message.success(`${actualizaciones.length} línea(s) relacionada(s) a "${insumoRelacionarTemp.stringBuscar}"`);
    }

    this.setState({
      relacionandoLote: false,
      seleccionGruposKeys: [],
      insumoRelacionarTemp: null,
      omitidasPorImpuestos: omitidas.map((o) => ({
        ...o,
        insumoIntentadoNombre: insumoRelacionarTemp.stringBuscar,
      })),
    });
  }

  // ---------- Paso 4: conceptos de todas las facturas (relacionar en lote) ----------

  // Muestra TODOS los conceptos de todas las facturas seleccionadas, agrupados, con su
  // estado de relación actual (ya relacionado / sin relacionar / mixto), para poder
  // seleccionar varios y relacionarlos (o corregirlos) de una sola vez a un mismo insumo.
  renderPasoConceptos() {
    const { seleccionGruposKeys, insumoRelacionarTemp, relacionandoLote, omitidasPorImpuestos } = this.state;
    const grupos = this.agruparTodasLasLineas();
    const totalConceptos = grupos.reduce((acc, g) => acc + g.lineas.length, 0);
    const relacionados = grupos.reduce((acc, g) => acc + (g.estadoInsumo === "relacionado" ? g.lineas.length : 0), 0);

    return (
      <>
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={`${relacionados} de ${totalConceptos} conceptos ya relacionados a un insumo`}
        />
        <p>Selecciona los conceptos que quieras relacionar (o cambiar) a un mismo insumo:</p>
        <Table
          size="small"
          rowKey="key"
          dataSource={grupos}
          pagination={false}
          rowSelection={{
            selectedRowKeys: seleccionGruposKeys,
            onChange: (keys) => this.setState({ seleccionGruposKeys: keys }),
          }}
          columns={[
            { title: "Concepto", render: (_, g) => `${g.descripcion || "-"} ${g.codigo ? `(${g.codigo})` : ""}`.trim() },
            { title: "Proveedor", dataIndex: "proveedorNombre" },
            {
              title: "Líneas",
              align: "right",
              render: (_, g) => (
                <Tooltip title={g.lineas.map((l) => l.facturaNombre).join(", ")}>{g.lineas.length}</Tooltip>
              ),
            },
            {
              title: "Impuestos XML",
              render: (_, g) => {
                const firmas = new Set(g.lineas.map((l) => JSON.stringify(normalizarFirmaConcepto(l.concepto.traslados))));
                if (firmas.size === 1) return textoTraslados(g.lineas[0].concepto.traslados);
                return (
                  <Tooltip title={g.lineas.map((l) => textoTraslados(l.concepto.traslados)).join(" · ")}>
                    <Tag color="gold">Varía</Tag>
                  </Tooltip>
                );
              },
            },
            {
              title: "Insumo relacionado",
              render: (_, g) => {
                if (g.estadoInsumo === "relacionado") return <Tag color="green">{g.insumoActual.stringBuscar}</Tag>;
                if (g.estadoInsumo === "mixto") return <Tag color="orange">Mixto</Tag>;
                return <Tag color="gold">Sin relacionar</Tag>;
              },
            },
          ]}
        />

        <Row gutter={8} align="middle" style={{ marginTop: 12 }}>
          <Col flex="auto">
            <SelectInsumosItem
              empresaId={this.props.empresaId}
              headersToken={this.props.headersToken}
              tiposInsumos="compra"
              placeholder="Insumo al que se relacionarán los seleccionados"
              width="100%"
              value={insumoRelacionarTemp?._id}
              insumoPreCargado={insumoRelacionarTemp}
              onChange={(value, insumo) => this.setState({ insumoRelacionarTemp: insumo })}
            />
          </Col>
          <Col>
            <ButtonItem
              type="primary"
              loading={relacionandoLote}
              disabled={seleccionGruposKeys.length === 0 || !insumoRelacionarTemp}
              onClick={() => this.relacionarGruposSeleccionados()}
            >
              Relacionar seleccionados ({seleccionGruposKeys.length})
            </ButtonItem>
          </Col>
        </Row>

        {omitidasPorImpuestos.length > 0 && (
          <Alert
            style={{ marginTop: 12 }}
            type="error"
            showIcon
            message={`${omitidasPorImpuestos.length} línea(s) no se relacionaron por impuestos distintos`}
            description={omitidasPorImpuestos.map((o, i) => (
              <div key={i}>
                <strong>{o.concepto.descripcion}</strong> ({o.facturaNombre}): impuestos de la línea{" "}
                {textoTraslados(o.concepto.traslados)} no coinciden con los del insumo "{o.insumoIntentadoNombre}"
              </div>
            ))}
          />
        )}

        <Row justify="space-between" style={{ marginTop: 20 }}>
          <ButtonItem onClick={() => this.setState({ paso: 2 })}>Atrás</ButtonItem>
          <ButtonItem type="primary" onClick={() => this.setState({ paso: 4 })}>Siguiente</ButtonItem>
        </Row>
      </>
    );
  }

  renderPaso5() {
    const { filas, guardando, progreso, resultados, terminado } = this.state;

    if (terminado) {
      const exitosas = resultados.filter((r) => r.ok).length;
      const fallidas = resultados.filter((r) => !r.ok).length;
      return (
        <Result
          status={fallidas === 0 ? "success" : "warning"}
          title={`Se guardaron ${exitosas} de ${resultados.length} facturas`}
          subTitle={fallidas > 0 ? `${fallidas} factura(s) no se pudieron guardar, revisa el detalle abajo` : undefined}
          extra={[
            <ButtonItem key="volver" type="primary" onClick={() => this.props.history.push("/panel/facturassinordendecompra")}>
              Volver a Facturas
            </ButtonItem>,
          ]}
        >
          {fallidas > 0 && (
            <Table
              size="small"
              rowKey="uid"
              dataSource={resultados.filter((r) => !r.ok)}
              pagination={false}
              columns={[
                { title: "Archivo", dataIndex: "nombre" },
                { title: "Motivo", dataIndex: "motivo" },
              ]}
            />
          )}
        </Result>
      );
    }

    const incluidas = filas.filter((f) => f.incluir).length;

    return (
      <>
        <Table
          size="small"
          rowKey="uid"
          dataSource={filas}
          pagination={false}
          columns={[
            {
              title: "",
              render: (_, r) => (
                <Checkbox
                  checked={r.incluir}
                  disabled={r.filaEstado === "error" || r.filaEstado === "faltaAsignar" || r.filaEstado === "duplicado" || guardando}
                  onChange={(e) => this.marcarFila(r.uid, e.target.checked)}
                />
              ),
            },
            { title: "Archivo", dataIndex: "nombre" },
            { title: "Proveedor", dataIndex: "proveedorNombre" },
            { title: "UUID", render: (_, r) => r.datos?.uuid },
            { title: "Total XML", render: (_, r) => r.datos?.totalXml?.toFixed(2) },
            {
              title: "Estado",
              render: (_, r) => {
                if (r.filaEstado === "nuevo") return <Tag color="green">Nueva</Tag>;
                if (r.filaEstado === "duplicado") return <Tag color="orange">Duplicada (UUID)</Tag>;
                if (r.filaEstado === "faltaAsignar") return <Tooltip title={r.motivoFila}><Tag color="gold">Falta asignar insumo</Tag></Tooltip>;
                return <Tooltip title={r.motivoFila}><Tag color="red">Error</Tag></Tooltip>;
              },
            },
          ]}
        />

        {guardando && (
          <Progress percent={Math.round((progreso.actual / progreso.total) * 100)} style={{ marginTop: 16 }} />
        )}

        <Row justify="space-between" style={{ marginTop: 20 }}>
          {/* paso:3 es el paso de Conceptos (renderPasoConceptos) tras el reordenamiento de pasos */}
          <ButtonItem disabled={guardando} onClick={() => this.setState({ paso: 3 })}>Atrás</ButtonItem>
          <ButtonItem type="primary" loading={guardando} disabled={incluidas === 0} onClick={() => this.guardarTodas()}>
            Guardar {incluidas} factura(s)
          </ButtonItem>
        </Row>
      </>
    );
  }

  render() {
    const { paso, loading, terminado } = this.state;

    return (
      <>
        <PageHeader
          title="Importar Facturas XML"
          onBack={() => this.props.history.push("/panel/facturassinordendecompra")}
          style={{ border: "1px solid rgb(235, 237, 240)", backgroundColor: "white", marginBottom: 10 }}
        />
        <div className="card-background" style={{ paddingTop: 5 }}>
          <Spin spinning={loading}>
            <Steps current={paso} size="small" style={{ padding: "16px 0 20px" }}>
              <Step title="XMLs" description="Elegir facturas" />
              <Step title="Proveedor" description="Tipo de proveedor" />
              <Step title="Proyecto" description="Proyecto del lote" />
              <Step title="Conceptos" description="Relacionar insumos" />
              <Step title="Revisión" description="Guardar" icon={terminado ? <CheckCircleOutlined /> : undefined} />
            </Steps>

            {paso === 0 && this.renderPaso1()}
            {paso === 1 && this.renderPaso2()}
            {paso === 2 && this.renderPasoProyecto()}
            {paso === 3 && this.renderPasoConceptos()}
            {paso === 4 && this.renderPaso5()}
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  vencimientoDesdeRecepcionFactura: state.empresa.vencimientoDesdeRecepcionFactura,
});

export default connect(mapStateToProps)(ImportarFacturasXmlMasivo);
